export const defaultThemeValues = {
    "appearance.mode": "system",
    "appearance.colorScheme": "light",
    "appearance.contrastMode": "system",
    "brand.primary": "#1769aa",
    "brand.secondary": "#087e8b",
    "brand.logoReference": "asset://inteacc/logo/default",
    "brand.productIdentity": "Inteacc",
    "brand.tenantDisplayName": "",
    "color.page.background": "#f7f9fc",
    "color.surface.default": "#ffffff",
    "color.surface.raised": "#ffffff",
    "color.surface.sunken": "#edf1f7",
    "color.surface.overlay": "#ffffff",
    "color.text.primary": "#152536",
    "color.text.secondary": "#3d4d5c",
    "color.text.muted": "#52606d",
    "color.text.inverse": "#ffffff",
    "color.text.disabled": "#7f8d9b",
    "color.text.link": "#0b4776",
    "color.interaction.hover": "#eef5ff",
    "color.interaction.selected": "#d9e9ff",
    "color.interaction.focus": "#1769aa",
    "color.interaction.disabled": "#edf1f7",
    "color.interaction.drag": "#0f568f",
    "color.status.information": "#1769aa",
    "color.status.success": "#16794b",
    "color.status.warning": "#a15c00",
    "color.status.error": "#b42318",
    "color.status.neutral": "#52606d",
    "color.border.default": "#9aa6b2",
    "color.border.subtle": "#edf1f7",
    "color.border.strong": "#52606d",
    "color.border.focus": "#1769aa",
    "color.border.error": "#b42318",
    "typography.family.primary": 'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    "typography.family.monospace": 'ui-monospace, "SFMono-Regular", Consolas, "Liberation Mono", monospace',
    "typography.scale": 1,
    "typography.weight.regular": 400,
    "typography.weight.medium": 600,
    "typography.weight.bold": 700,
    "typography.lineHeight": 1.5,
    "density.mode": "standard",
    "spacing.scale": 1,
    "control.height": 40,
    "grid.rowHeight": 48,
    "visualization.cardStyle": "outlined",
    "visualization.hoverEffect": "none",
    "ui.buttonStyle": "solid",
    "ui.formStyle": "outlined",
    "ui.tableStyle": "standard",
    "ui.navigationStyle": "standard",
    "shape.radius.small": 6,
    "shape.radius.medium": 10,
    "shape.radius.large": 16,
    "motion.enabled": true,
    "motion.reduced": false,
    "motion.durationScale": 1,
    "accessibility.highContrast": false,
    "accessibility.reducedMotion": false,
    "accessibility.enhancedFocus": false,
    "accessibility.colorVisionMode": "none",
    "chart.seriesPalette": [
        "#1769aa",
        "#16794b",
        "#a15c00",
        "#704264",
        "#087e8b",
        "#b42318",
    ],
    "chart.axis": "#52606d",
    "chart.grid": "#edf1f7",
    "chart.label": "#152536",
    "chart.tooltip": "#ffffff",
    "chart.decalMode": "auto",
};
const capabilityByKey = (key) => {
    if (key.startsWith("appearance."))
        return "appearance";
    if (key.startsWith("brand."))
        return "branding";
    if (key.startsWith("typography."))
        return "typography";
    if (key.startsWith("spacing."))
        return "spacing";
    if (key.startsWith("density."))
        return "density";
    if (key.startsWith("visualization."))
        return "charts";
    if (key.startsWith("ui."))
        return "appearance";
    if (key.startsWith("shape."))
        return "shape";
    if (key.startsWith("motion."))
        return "motion";
    if (key.startsWith("accessibility."))
        return "accessibility";
    if (key.startsWith("chart."))
        return "charts";
    return "color";
};
const protectedKeys = new Set([
    "brand.logoReference",
    "brand.productIdentity",
    "typography.family.primary",
]);
const tenantEligibleKeys = new Set([
    "brand.primary",
    "brand.secondary",
    "brand.logoReference",
    "brand.tenantDisplayName",
    "typography.family.primary",
    "typography.scale",
    "shape.radius.small",
    "shape.radius.medium",
    "shape.radius.large",
    "density.mode",
]);
const roleEligibleKeys = new Set([
    "appearance.contrastMode",
    "typography.scale",
    "density.mode",
    "motion.enabled",
    "motion.reduced",
    "motion.durationScale",
    "accessibility.highContrast",
    "accessibility.reducedMotion",
    "accessibility.enhancedFocus",
    "accessibility.colorVisionMode",
]);
const roamingKeys = new Set([
    "accessibility.highContrast",
    "accessibility.reducedMotion",
    "accessibility.enhancedFocus",
    "accessibility.colorVisionMode",
]);
const userForbiddenKeys = new Set([
    "brand.logoReference",
    "brand.productIdentity",
    "brand.tenantDisplayName",
    "typography.family.primary",
    "typography.family.monospace",
]);
const screenEligibleKeys = new Set([
    "appearance.mode",
    "appearance.contrastMode",
    "typography.scale",
    "density.mode",
    "motion.enabled",
    "motion.reduced",
    "motion.durationScale",
    "accessibility.highContrast",
    "accessibility.reducedMotion",
    "accessibility.enhancedFocus",
    "accessibility.colorVisionMode",
]);
const previewControl = (key) => {
    if (key === "appearance.mode")
        return "mode";
    if (key.includes("color") || key.startsWith("brand.")) {
        return key === "brand.logoReference" ||
            key === "brand.productIdentity" ||
            key === "brand.tenantDisplayName"
            ? "readonly"
            : "color";
    }
    if (key === "chart.seriesPalette")
        return "palette";
    const value = defaultThemeValues[key];
    if (typeof value === "boolean")
        return "boolean";
    if (typeof value === "number")
        return "number";
    return "select";
};
export const themePropertyKeys = Object.freeze(Object.keys(defaultThemeValues));
export const themePropertyDefinitions = themePropertyKeys.map((key) => ({
    key,
    capabilityId: capabilityByKey(key),
    maturity: "preview",
    defaultValue: defaultThemeValues[key],
    protected: protectedKeys.has(key),
    tenantOverrideEligible: tenantEligibleKeys.has(key),
    roleOverrideEligible: roleEligibleKeys.has(key),
    userOverrideEligible: !userForbiddenKeys.has(key),
    screenOverrideEligible: screenEligibleKeys.has(key),
    crossProductRoamingEligible: roamingKeys.has(key),
    previewControl: previewControl(key),
}));
const capabilityIds = [
    "appearance",
    "branding",
    "color",
    "typography",
    "spacing",
    "density",
    "shape",
    "motion",
    "accessibility",
    "navigation",
    "forms",
    "data-grid",
    "dialogs",
    "notifications",
    "charts",
];
export const themeCapabilities = capabilityIds.map((capabilityId) => ({
    capabilityId,
    maturity: "preview",
    propertyKeys: themePropertyDefinitions
        .filter((definition) => definition.capabilityId === capabilityId)
        .map((definition) => definition.key),
    previewGroup: capabilityId === "data-grid"
        ? "Data grid"
        : capabilityId.charAt(0).toUpperCase() + capabilityId.slice(1),
}));
export const themeCapabilityRegistry = {
    capabilities: themeCapabilities,
    getCapability: (capabilityId) => themeCapabilities.find((capability) => capability.capabilityId === capabilityId),
    getPropertyDefinition: (propertyKey) => themePropertyDefinitions.find((definition) => definition.key === propertyKey),
    isRegistered: (propertyKey) => themePropertyKeys.includes(propertyKey),
};
export const protectedThemeProperties = [
    {
        propertyKey: "brand.logoReference",
        establishedBy: ["brand", "product", "tenant"],
        allowedOverrideLayers: ["brand", "product", "tenant"],
        reason: "Approved logos are controlled brand assets.",
    },
    {
        propertyKey: "brand.productIdentity",
        establishedBy: ["brand", "product"],
        allowedOverrideLayers: ["brand", "product"],
        reason: "Product identity is controlled by Inteacc and the product.",
    },
    {
        propertyKey: "typography.family.primary",
        establishedBy: ["brand", "product", "tenant"],
        allowedOverrideLayers: ["brand", "product", "tenant"],
        reason: "Typography identity uses approved brand and tenant fonts.",
    },
];
export const defaultTenantBrandingPolicy = {
    policyId: "iaf-tenant-branding-v1",
    allowedProperties: [...tenantEligibleKeys],
    allowedFontFamilies: [
        "Inter",
        "Arial",
        "Helvetica",
        "Noto Sans",
        "Source Sans 3",
        "system-ui",
    ],
    assetPolicy: {
        allowedTypes: ["logo", "favicon", "font"],
        maximumBytes: 2_000_000,
        minimumWidth: 32,
        minimumHeight: 32,
        maximumAspectRatio: 8,
        permittedReferenceSchemes: ["asset"],
        permittedOrigins: ["inteacc", "tenant"],
    },
};
export const layerOrder = {
    iaf: 0,
    brand: 1,
    product: 2,
    tenant: 3,
    role: 4,
    "user-accessibility": 5,
    user: 6,
    screen: 7,
    preview: 8,
};
export const canLayerOverrideProperty = (layer, propertyKey, tenantPolicy = defaultTenantBrandingPolicy) => {
    const definition = themeCapabilityRegistry.getPropertyDefinition(propertyKey);
    if (!definition)
        return false;
    if (layer === "iaf" || layer === "brand" || layer === "product")
        return true;
    if (layer === "tenant") {
        return (definition.tenantOverrideEligible &&
            tenantPolicy.allowedProperties.includes(propertyKey));
    }
    if (layer === "role")
        return definition.roleOverrideEligible;
    if (layer === "user-accessibility") {
        return definition.crossProductRoamingEligible;
    }
    if (layer === "user")
        return definition.userOverrideEligible;
    if (layer === "screen")
        return definition.screenOverrideEligible;
    return layer === "preview" && definition.userOverrideEligible;
};
//# sourceMappingURL=catalog.js.map